import React, { useEffect, useState } from "react";
import styles from "./AppDownloadPeek.module.scss";
import { remoteConfig } from "services/remoteConfig";
import { mergeTvAppPromptConfig, resolveTvAppPlatform, openAppStore, isPromptDismissed, markPromptDismissed } from "constants/TvAppPrompt";
import { pickImage } from "helpers/webpSupport";

import fireTvIcon from "images/icons/FireTV-Icon.png";
import fireTvIconWebp from "images/icons/FireTV-Icon.webp";
import rokuIcon from "images/icons/Roku-Icon-v2.png";
import rokuIconWebp from "images/icons/Roku-Icon-v2.webp";
import crossIcon from "images/icons/Cross.png";

const PLATFORM_ICONS = {
    firetv: { webp: fireTvIconWebp, fallback: fireTvIcon },
    roku: { webp: rokuIconWebp, fallback: rokuIcon },
};

const dismissKey = (platform) => `app_prompt_dismissed_${platform}`;

/**
 * Floating "Download our app" tab pinned to the right edge, shown only to
 * browsers on TV devices that have a native Game City app (FireTV via Amazon
 * Silk, Roku). Config comes from remote-config key TvAppPromptConfig (admin
 * TV Config page) merged over bundled defaults. Dismissal is remembered per
 * platform in localStorage and re-shows after config.reShowDays.
 * Mounted once globally in Layout.
 */
const AppDownloadPeek = () => {
    const [prompt, setPrompt] = useState(null); // { platform, label, url, deepLinkUrl }
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let cancelled = false;
        // Wait for remote config (already loading at app startup) before gating.
        remoteConfig.load().then(() => {
            if (cancelled) return;
            const platform = resolveTvAppPlatform();
            if (!platform) return;
            const config = mergeTvAppPromptConfig(remoteConfig.getTvAppPrompt());
            const entry = config.platforms[platform];
            if (!config.enabled || !entry?.enabled || !entry.url) return;
            if (isPromptDismissed(dismissKey(platform), config.reShowDays)) return;
            setPrompt({ platform, ...entry });
        });
        return () => { cancelled = true; };
    }, []);

    // Slide in shortly after we know there's something to show.
    useEffect(() => {
        if (!prompt) return undefined;
        const t = window.setTimeout(() => setVisible(true), 2000);
        return () => window.clearTimeout(t);
    }, [prompt]);

    if (!prompt) return null;

    const openStore = () => openAppStore(prompt);

    const dismiss = (e) => {
        e.stopPropagation();
        markPromptDismissed(dismissKey(prompt.platform));
        setVisible(false);
        // Unmount after the slide-out transition finishes.
        window.setTimeout(() => setPrompt(null), 450);
    };

    const icon = PLATFORM_ICONS[prompt.platform];

    return (
        <div
            className={`${styles.peek} ${visible ? styles.visible : ""}`}
            onClick={openStore}
            role="button"
            tabIndex={visible ? 0 : -1}
            aria-hidden={!visible}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openStore()}
            aria-label={prompt.label}
        >
            <div className={styles.panel}>
                {icon && <img src={pickImage(icon.webp, icon.fallback)} className={styles.icon} alt="" draggable={false} />}
                <div className={styles.body}>
                    <span className={styles.title}>{prompt.label}</span>
                    <span className={styles.cta}>Free on your TV &rsaquo;</span>
                </div>
                <button className={styles.dismiss} onClick={dismiss} aria-label="Dismiss">
                    <img src={crossIcon} alt="" draggable={false} />
                </button>
            </div>
        </div>
    );
};

export default AppDownloadPeek;
